import {
  AnimationComponent,
  Box,
  Grid2,
  LockIcon,
  Show,
  Tab,
  Tabs,
  Typography,
} from "components";
import DashboardLayout from "layouts/DashboardLayout";
import { lazy } from "react";
import { TAB } from "store/slices/appSlice";
import { useAppDispatch, useAppSelector } from "store/store";
import { GET_ALL_EQUITY_ETF_TRANSACTION } from "utils/apiRoutes";
import { QueryKeys } from "utils/queryKey";

const AvailableCash = lazy(
  () => import("Pages/CashManagement/AvailableCashBalance"),
);
const BankLedger = lazy(() => import("Pages/BankLedger/Index"));
const CashManagementMainPage = lazy(() => import("Pages/CashManagement/index"));
const IncomeExpense = lazy(() => import("Pages/IncomeExpense"));
const MutualFunds = lazy(() => import("Pages/MutualFunds"));
const Transaction = lazy(() => import("Pages/EquityEtf"));
const CapitalGain = lazy(() => import("Pages/CapitalGain"));
const ReportTab = lazy(() => import("Pages/ReportManager/ReportTab"));

const ReportsMenu = () => {
  const dispatch = useAppDispatch();

  const userData = useAppSelector((state) => state?.user?.userData);
  const { tabValueOfReports } = useAppSelector((state) => state?.app?.tab);

  const tabs = [
    {
      canAccess: true,
      label: "Report Manager",
      component: <ReportTab />,
    },
    {
      canAccess: userData?.grantAccess,
      label: "Ledger",
      component: <BankLedger />,
    },
    {
      canAccess: userData?.grantAccess,
      label: "Ledger Balance",
      component: <AvailableCash />,
    },
    {
      label: "Capital Gain",
      component: <CapitalGain />,
      canAccess: true,
    },
    {
      canAccess: true,
      label: "Equity/ETF",
      component: (
        <Transaction
          title={"Equity/ETF"}
          name={"tradeBook"}
          apiRoute={GET_ALL_EQUITY_ETF_TRANSACTION}
          apiRouteKey={QueryKeys.TRANSACTIONS_LIST}
          tableName="trade_book_list"
          bulkDelete={"/transaction/bulk_delete/"}
          routeNavigate={{
            navigate: "/app/create-transaction",
            transactionType: "EQUITY",
            assetType: ["EQ", "ETF"],
            editNavigate: "/app/edit-transaction",
          }}
        />
      ),
    },
    {
      canAccess: userData?.grantAccess,
      label: "Mutual Funds",
      component: <MutualFunds />,
    },
    {
      canAccess: true,
      label: "Cash Entry",
      component: <CashManagementMainPage />,
    },
    {
      canAccess: userData?.grantAccess,
      label: "Income/Expense",
      component: <IncomeExpense />,
    },
    {
      canAccess: userData?.grantAccess,
      label: "Private Equity",
      component: (
        <Transaction
          title={"Private Equity"}
          name={"privateEquityList"}
          apiRoute={"/other_assets/"}
          requestParams={{
            key: "asset_type",
            value: "PEQ",
          }}
          apiRouteKey={QueryKeys.PRIVATE_EQUITY_LIST}
          tableName="private_equity_list"
          routeNavigate={{
            navigate: "/app/create-transaction",
            transactionType: "OTHER-ASSETS",
            assetType: ["PEQ"],
            editNavigate: "/app/edit-transaction",
          }}
          bulkDelete={"/other_assets/bulk_delete/?asset_type=PEQ"}
        />
      ),
    },
    {
      canAccess: userData?.grantAccess,
      label: "Fixed Deposit",
      component: (
        <Transaction
          title={"Fixed Deposit"}
          name={"FDList"}
          apiRoute={"/other_assets/"}
          requestParams={{
            key: "asset_type",
            value: "FD",
          }}
          apiRouteKey={QueryKeys.FD_LIST}
          tableName="FD_list"
          routeNavigate={{
            navigate: "/app/create-fixed-deposit-transaction",
            transactionType: "OTHER-ASSETS",
            assetType: "FD",
            editNavigate: "/app/edit-fixed-deposit-transaction",
          }}
          bulkDelete={"/other_assets/bulk_delete/?asset_type=FD"}
        />
      ),
    },
    {
      canAccess: userData?.grantAccess,
      label: "AIF",
      component: (
        <Transaction
          title={"AIF"}
          name={"aif"}
          apiRoute={"/other_assets/"}
          requestParams={{
            key: "asset_type",
            value: "AIF",
          }}
          apiRouteKey={QueryKeys.AIF_LIST}
          tableName="aif_list"
          routeNavigate={{
            navigate: "/app/create-transaction",
            transactionType: "OTHER-ASSETS",
            assetType: ["AIF"],
            editNavigate: "/app/edit-transaction",
          }}
          bulkDelete={"/other_assets/bulk_delete/?asset_type=AIF"}
        />
      ),
    },
    // {
    //   canAccess: userData?.grantAccess,
    //   label: "Positions",
    //   component: (
    //     <Transaction
    //       title={"Position"}
    //       name={"positions"}
    //       apiRoute={"/other_assets/"}
    //       requestParams={{
    //         key: "asset_type",
    //         value: "OPTIONS",
    //       }}
    //       apiRouteKey={QueryKeys.OPTIONS}
    //       tableName="position_list"
    //       routeNavigate={{
    //         navigate: "/app/create-transaction",
    //         transactionType: "OTHER-ASSETS",
    //         assetType: ["FUTURES", "OPTIONS"],
    //         editNavigate: "/app/edit-transaction",
    //       }}
    //       bulkDelete={"/other_assets/bulk_delete/?asset_type=OPTIONS"}
    //     />
    //   ),
    // },
  ];

  return (
    <DashboardLayout title="Dashboard">
      <Grid2 container spacing={2}>
        <Grid2 size={12}>
          <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
            <Tabs
              sx={{
                "& .MuiTabs-flexContainer": {
                  backgroundColor: "var(--white)",
                },
              }}
              onChange={(e, val) => {
                dispatch(TAB({ tabKey: "tabValueOfReports", tabValue: val }));
              }}
              value={tabValueOfReports}
              variant="scrollable"
              scrollButtons={false}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={index}
                  label={
                    <Box display={"flex"} gap={1}>
                      <Typography>{tab.label}</Typography>
                      {!tab.canAccess && <LockIcon width={16} height={16} />}
                    </Box>
                  }
                  disabled={!tab.canAccess}
                  disableTouchRipple
                />
              ))}
            </Tabs>
          </Box>
        </Grid2>
        <Grid2 size={12}>
          {tabs.map((tab, index) => (
            <Show.When key={index} isTrue={tabValueOfReports === index}>
              <AnimationComponent>{tab.component}</AnimationComponent>
            </Show.When>
          ))}
        </Grid2>
      </Grid2>
    </DashboardLayout>
  );
};

export default ReportsMenu;
